import { useCallback, useState } from "react";

import {
  type DraftRow,
  reorderRows,
  type StatementType,
} from "@/features/classification/lib/statement-tree";

/** Local, unsaved statement-tree rows with the edits the rows editor offers. */
export function useDraftRows() {
  const [rows, setRows] = useState<DraftRow[]>([]);

  const add = useCallback((statementType: StatementType) => {
    setRows((current) => [
      ...current,
      {
        key: crypto.randomUUID(),
        // an id is derived from the labels at save, so a row that has none yet
        // does not get stuck with one built from empty text
        row_id: null,
        statement_type: statementType,
        // debit is the common case on both statements: the packaged taxonomy is
        // 20 of 23 on P&L (only revenue and financial income are credit)
        sign: 1,
        level_1: "",
        level_2: "",
        level_3: null,
      },
    ]);
  }, []);

  const update = useCallback((key: string, patch: Partial<DraftRow>) => {
    setRows((current) =>
      current.map((row) => (row.key === key ? { ...row, ...patch } : row)),
    );
  }, []);

  const reorder = useCallback((fromKey: string, toKey: string) => {
    setRows((current) => reorderRows(current, fromKey, toKey));
  }, []);

  const remove = useCallback((key: string) => {
    setRows((current) => current.filter((row) => row.key !== key));
  }, []);

  return { rows, setRows, add, update, reorder, remove };
}
